import AppValues from 'src/app/common/app.values';
import ProjectLinks from '../../../common/project.links';
import { InitiatorRole } from '../../profile/profile.enum';
import {
  CommentReplyNotification,
  ConnectionInvitationNotification,
  InviteToProjectAcceptedNotification,
  NotificationEvent,
  NotificationMessagePayload,
  ProjectInvitationNotification,
  ProjectMemberExcludedNotification,
  ProjectMemberLeftNotification,
  RequestToJoinProjectNotification,
} from '../interfaces';
import { BaseCommunityInterface } from '../interfaces/base-community.interface';
import { CategoryRenamedPayload } from '../interfaces/category-renamed.interface';
import { DoctorPatientInvitationNotification } from '../interfaces/doctor-patient-link-invitation.interface';
import { FileSharedNotification } from '../interfaces/file-shared.interface';
import { InviteToProjectDeclineNotification } from '../interfaces/invite-to-project-decline.interface';
import { EntityType, LeadPostCommentedInterface } from '../interfaces/lead-post-commented.interface';
import { PostCommentedInterface } from '../interfaces/post-commented.interface';
import { RoleRemovedEvent } from '../interfaces/role-removed-event';
import { UserUpdatedReactivationPetitionNotification } from '../interfaces/user-updated-reactivation-petition.interface';
import {
  createInviteToProjectMessage,
  createRemoveFromProjectMessage,
  doctorPatientLinkInitiatorError,
  eventBasedCommunityLink,
} from '../notification.helpers';
import { CreateViewNotificationBody, EventNotificationsStrategy } from './event-notifications.interface';
import { NOTIFICATION_COMPONENT_TYPES, NOTIFICATION_TEXTS } from './notifications.types';

export class EventNotificationsContext {
  private strategy: EventNotificationsStrategy;

  public constructor(strategy: EventNotificationsStrategy) {
    this.strategy = strategy;
  }

  public doCreateNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    return this.strategy.createNotificationMessagePayload(notification);
  }

  public doCreateRouterUrl(notification: NotificationEvent): string | null {
    if (this.strategy.createRouterUrl) {
      return this.strategy.createRouterUrl(notification);
    }

    return null;
  }
}

export class UserGetConnectionInvitationNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: ConnectionInvitationNotification = notification.message_data as ConnectionInvitationNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['USER_GET_CONNECTION_INVITATION'] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['USER_GET_CONNECTION_INVITATION'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as ConnectionInvitationNotification).initiator_account_id
    }`;
  }
}

export class UserGetDoctorPatientLinkInvitation implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: DoctorPatientInvitationNotification = notification.message_data as DoctorPatientInvitationNotification;

    const initiatorRole: (role: string) => string | never = (role: string) => {
      if (role === InitiatorRole.doctor) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ADDED'][0];
      } else if (role === InitiatorRole.patient) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ADDED'][1];
      } else {
        return doctorPatientLinkInitiatorError(role);
      }
    };

    return [
      {
        text: initiatorRole(data.initiator_role),
      },
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ADDED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['DOCTOR_PATIENT_LINK_REQUEST_ADDED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        entityId: (notification.message_data as DoctorPatientInvitationNotification).doctor_patient_link_id,
        messageSid: notification.message_sid,
        actions: {
          confirmAction: 'confirmTestAction',
          rejectAction: 'rejectTestAction',
        },
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as DoctorPatientInvitationNotification).initiator_account_id
    }`;
  }
}

export class DoctorPatientLinkAcceptedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: DoctorPatientInvitationNotification = notification.message_data as DoctorPatientInvitationNotification;
    const initiatorRole: (role: string) => string | never = (role: string) => {
      if (role === InitiatorRole.doctor) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ACCEPTED'][0];
      } else if (role === InitiatorRole.patient) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ACCEPTED'][1];
      } else {
        return doctorPatientLinkInitiatorError(role);
      }
    };

    return [
      {
        text: initiatorRole(data.initiator_role),
      },
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_ACCEPTED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['DOCTOR_PATIENT_LINK_REQUEST_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as DoctorPatientInvitationNotification).initiator_account_id
    }`;
  }
}

export class DoctorPatientLinkRejectedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: DoctorPatientInvitationNotification = notification.message_data as DoctorPatientInvitationNotification;

    const initiatorRole: (role: string) => string | never = (role: string) => {
      if (role === InitiatorRole.doctor) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REJECTED'][0];
      } else if (role === InitiatorRole.patient) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REJECTED'][1];
      } else {
        return doctorPatientLinkInitiatorError(role);
      }
    };

    return [
      {
        text: initiatorRole(data.initiator_role),
      },
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REJECTED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['DOCTOR_PATIENT_LINK_REQUEST_REJECTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as DoctorPatientInvitationNotification).initiator_account_id
    }`;
  }
}

export class DoctorPatientLinkRemovedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: DoctorPatientInvitationNotification = notification.message_data as DoctorPatientInvitationNotification;

    const initiatorRole: (initiatorRole: string) => string | never = (role: string) => {
      if (role === InitiatorRole.doctor) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REMOVED'][0];
      } else if (role === InitiatorRole.patient) {
        return NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REMOVED'][1];
      } else {
        return doctorPatientLinkInitiatorError(role);
      }
    };

    return [
      {
        text: initiatorRole(data.initiator_role),
      },
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['DOCTOR_PATIENT_LINK_REQUEST_REMOVED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['DOCTOR_PATIENT_LINK_REQUEST_REMOVED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as DoctorPatientInvitationNotification).initiator_account_id
    }`;
  }
}

export class UserGetProjectInvitationNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: ProjectInvitationNotification = notification.message_data as ProjectInvitationNotification;
    return createInviteToProjectMessage(data, NOTIFICATION_TEXTS['INVITE_TO_PROJECT']);
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_PROJECT'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        entityId: (notification.message_data as ProjectInvitationNotification).project_id,
        messageSid: notification.message_sid,
        actions: {
          confirmAction: 'confirmTestAction',
          rejectAction: 'rejectTestAction',
        },
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const message_data: ProjectInvitationNotification = notification.message_data as ProjectInvitationNotification;

    if (message_data.is_private) {
      return `${ProjectLinks.LINK_PROFILE_VIEW}/${message_data.initiator_account_id}`;
    } else {
      return `${ProjectLinks.LINK_PROJECTS_VIEW}/${message_data.project_id}`;
    }
  }
}

export class UserGetRequestToJoinProject implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: RequestToJoinProjectNotification = notification.message_data as RequestToJoinProjectNotification;
    return [
      {
        text: data.participant_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.participant_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT'][0] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['REQUEST_TO_JOIN_PROJECT'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        entityId: (notification.message_data as RequestToJoinProjectNotification).project_id,
        messageSid: notification.message_sid,
        actions: {
          confirmAction: 'confirmTestAction',
          rejectAction: 'rejectTestAction',
        },
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as RequestToJoinProjectNotification).participant_account_id
    }`;
  }
}

export class UserGetRequestToProjectAcccepted implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: RequestToJoinProjectNotification = notification.message_data as RequestToJoinProjectNotification;
    return [
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT_ACCEPTED'] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['REQUEST_TO_JOIN_PROJECT_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as RequestToJoinProjectNotification).project_id
    }`;
  }
}

export class UserGetConnectionInvitationAcceptsNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: ConnectionInvitationNotification = notification.message_data as ConnectionInvitationNotification;
    return [
      {
        text: data.connection_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.connection_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['USER_CONNECTION_INVITATION_ACCEPTS'] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['USER_CONNECTION_INVITATION_ACCEPTS'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as ConnectionInvitationNotification).connection_account_id
    }`;
  }
}

export class InviteToProjectAccepted implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: InviteToProjectAcceptedNotification = notification.message_data as InviteToProjectAcceptedNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_ACCEPTED'][0] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_ACCEPTED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_PROJECT_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as InviteToProjectAcceptedNotification).project_id
    }`;
  }
}

export class InviteToProjectDecline implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: InviteToProjectDeclineNotification = notification.message_data as InviteToProjectDeclineNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_DECLINE'][0] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_DECLINE'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_PROJECT_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as InviteToProjectDeclineNotification).project_id
    }`;
  }
}

export class UserGetRequestToProjectAccceptedByAnyOwner implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: InviteToProjectAcceptedNotification = notification.message_data as InviteToProjectAcceptedNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_ACCEPTED_BY_ANY_OWNER'][0] },
      {
        text: data.participant_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.participant_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_ACCEPTED_BY_ANY_OWNER'][1] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_ACCEPTED_BY_ANY_OWNER'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_PROJECT_ACCEPTED_BY_ANY_OWNER'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as InviteToProjectAcceptedNotification).project_id
    }`;
  }
}

export class UserGetRequestToProjectDeclinedByAnyOwner implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: InviteToProjectDeclineNotification = notification.message_data as InviteToProjectDeclineNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_DECLINE_BY_ANY_OWNER'][0] },
      {
        text: data.participant_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.participant_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_DECLINE_BY_ANY_OWNER'][1] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_PROJECT_DECLINE_BY_ANY_OWNER'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_PROJECT_ACCEPTED_BY_ANY_OWNER'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as InviteToProjectDeclineNotification).project_id
    }`;
  }
}

export class UserGetRequestToProjectDecline implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: RequestToJoinProjectNotification = notification.message_data as RequestToJoinProjectNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT_DECLINED'][0] },
      {
        text: data.participant_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.participant_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT_DECLINED'][1] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_PROJECT_DECLINED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['REQUEST_TO_JOIN_PROJECT_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }
}

export class UserGetProjectMemberLeftNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: ProjectMemberLeftNotification = notification.message_data as ProjectMemberLeftNotification;
    return [
      {
        text: data.member_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.member_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['PROJECT_MEMBER_LEFT'] },
      {
        text: data.project_title,
        href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.project_id}`,
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['PROJECT_MEMBER_LEFT'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROJECTS_VIEW}/${
      (notification.message_data as ProjectMemberLeftNotification).project_id
    }`;
  }
}

export class UserGetProjectMemberExcludedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: ProjectMemberExcludedNotification = notification.message_data as ProjectMemberExcludedNotification;
    return createRemoveFromProjectMessage(data, NOTIFICATION_TEXTS['PROJECT_MEMBER_EXCLUDED']);
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['PROJECT_MEMBER_EXCLUDED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const message_data: ProjectMemberExcludedNotification =
      notification.message_data as ProjectMemberExcludedNotification;

    if (message_data.is_private) {
      return `${ProjectLinks.LINK_PROFILE_VIEW}/${message_data.initiator_account_id}`;
    } else {
      return `${ProjectLinks.LINK_PROJECTS_VIEW}/${message_data.project_id}`;
    }
  }
}

export class UserGetCommentReplyNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: CommentReplyNotification = notification.message_data as CommentReplyNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['COMMENT_REPLY'] },
      {
        text: data.related_entity_name,
        href: `${ProjectLinks.LINK_POSTS_VIEW}/${data.related_entity_id}#${data.reply_id}`,
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['COMMENT_REPLY'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: CommentReplyNotification = notification.message_data as CommentReplyNotification;
    return `${ProjectLinks.LINK_POSTS_VIEW}/${data.related_entity_id}#${data.reply_id}`;
  }
}

export class LeadGetCommentReplyNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: LeadPostCommentedInterface = notification.message_data as LeadPostCommentedInterface;
    const text =
      data.parent_entity_type === EntityType.PROJECT
        ? NOTIFICATION_TEXTS['POST_LEAD_COMMENT'][1]
        : NOTIFICATION_TEXTS['POST_LEAD_COMMENT'][2];
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['POST_LEAD_COMMENT'][0] },
      {
        text: data.post_title,
        href: `${ProjectLinks.LINK_POSTS_VIEW}/${data.post_id}#${data.comment_id}`,
      },
      { text: text },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['POST_LEAD_COMMENT'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: LeadPostCommentedInterface = notification.message_data as LeadPostCommentedInterface;
    return `${ProjectLinks.LINK_POSTS_VIEW}/${data.post_id}#${data.comment_id}`;
  }
}

export class FileSharedEventNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: FileSharedNotification = notification.message_data as FileSharedNotification;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['FILE_SHARED'][0] },
      {
        text: data.file_name,
        fileId: data.file_descriptor_id,
      },
      { text: NOTIFICATION_TEXTS['FILE_SHARED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['FILE_SHARED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }
}

export class InvitationToJoinCommunityNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_COMMUNITY'][0] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
      { text: NOTIFICATION_TEXTS['INVITE_TO_COMMUNITY'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_COMMUNITY'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
        actions: {
          confirmAction: 'confirmTestAction',
          rejectAction: 'rejectTestAction',
        },
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class InvitationToJoinCommunityAcceptedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;

    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITATION_TO_JOIN_COMMUNITY_ACCEPTED'][0] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
      { text: NOTIFICATION_TEXTS['INVITATION_TO_JOIN_COMMUNITY_ACCEPTED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITATION_TO_JOIN_COMMUNITY_ACCEPTED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class InvitationToJoinCommunityRejectedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;

    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['INVITATION_TO_JOIN_COMMUNITY_DECLINED'][0] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
      { text: NOTIFICATION_TEXTS['INVITATION_TO_JOIN_COMMUNITY_DECLINED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITATION_TO_JOIN_COMMUNITY_DECLINED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class MemberLeftCommunityNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;

    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['MEMBER_LEFT_COMMUNITY'] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['MEMBER_LEFT_COMMUNITY'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class RequestToJoinCommunityNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;

    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_COMMUNITY'] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['INVITE_TO_COMMUNITY'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
        actions: {
          confirmAction: 'confirmTestAction',
          rejectAction: 'rejectTestAction',
        },
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`;
  }
}

export class CategoryRenamedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: CategoryRenamedPayload = notification.message_data as CategoryRenamedPayload;
    return [
      { text: NOTIFICATION_TEXTS['CATEGORY_RENAMED'][0] },
      { text: `"${data.previous_title}"` },
      { text: `${data.category_type}` },
      { text: NOTIFICATION_TEXTS['CATEGORY_RENAMED'][1] },
      { text: `"${data.new_title}"` },
      { text: NOTIFICATION_TEXTS['CATEGORY_RENAMED'][2] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['CATEGORY_RENAMED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }
}

export class RequestToJoinCommunityApprovedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;

    return [
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_COMMUNITY_APPROVED'] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['REQUEST_TO_JOIN_COMMUNITY_APPROVED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class RequestToJoinCommunityDeclinedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return [
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_COMMUNITY_DECLINED'][0] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
      { text: NOTIFICATION_TEXTS['REQUEST_TO_JOIN_COMMUNITY_DECLINED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['REQUEST_TO_JOIN_COMMUNITY_DECLINED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class CommunityMemberRemovedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['COMMUNITY_MEMBER_REMOVED'][0] },
      {
        text: data.community_title,
        href: eventBasedCommunityLink(data),
      },
      { text: NOTIFICATION_TEXTS['COMMUNITY_MEMBER_REMOVED'][1] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['COMMUNITY_MEMBER_REMOVED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: BaseCommunityInterface = notification.message_data as BaseCommunityInterface;
    return eventBasedCommunityLink(data);
  }
}

export class PostCommentedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: PostCommentedInterface = notification.message_data as PostCommentedInterface;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['POST_COMMENTED'] },
      {
        text: data.post_title,
        href: `${ProjectLinks.LINK_POSTS_VIEW}/${data.post_id}#${data.comment_id}`,
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['invite_to_community'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: PostCommentedInterface = notification.message_data as PostCommentedInterface;
    return `${ProjectLinks.LINK_POSTS_VIEW}/${data.post_id}#${data.comment_id}`;
  }
}

export class UnsupportedNotificationStrategy implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    return [
      {
        text: `Notification class '${notification.message_class}' is not yet supported.`,
      },
    ];
  }

  public createComponentNotificationData(_notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: undefined,
      notificationData: undefined,
    };
  }
}

export class UserUpdatedReactivationPetition implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: UserUpdatedReactivationPetitionNotification =
      notification.message_data as UserUpdatedReactivationPetitionNotification;

    return [
      { text: NOTIFICATION_TEXTS['USER_UPDATED_REACTIVATION_PETITION'] },
      {
        text: data.initiator_name,
        href: ProjectLinks.LINK_PROFILE_VIEW + '/' + data.initiator_account_id,
      },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    const data: UserUpdatedReactivationPetitionNotification =
      notification.message_data as UserUpdatedReactivationPetitionNotification;

    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['USER_UPDATED_REACTIVATION_PETITION'],

      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
        entityId: data.initiator_account_id,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    const data: UserUpdatedReactivationPetitionNotification =
      notification.message_data as UserUpdatedReactivationPetitionNotification;
    return ProjectLinks.LINK_DEACTIVATED_USERS + '#' + AppValues.reactivationTab + '/' + data.initiator_account_id;
  }
}

export class PatientRoleRemovedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: RoleRemovedEvent = notification.message_data as RoleRemovedEvent;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['PATIENT_ROLE_REMOVED'] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['PATIENT_ROLE_REMOVED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as ConnectionInvitationNotification).initiator_account_id
    }`;
  }
}

export class DoctorRoleRemovedNotification implements EventNotificationsStrategy {
  public createNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const data: RoleRemovedEvent = notification.message_data as RoleRemovedEvent;
    return [
      {
        text: data.initiator_name,
        href: `${ProjectLinks.LINK_PROFILE_VIEW}/${data.initiator_account_id}`,
      },
      { text: NOTIFICATION_TEXTS['DOCTOR_ROLE_REMOVED'] },
    ];
  }

  public createComponentNotificationData(notification: NotificationEvent): CreateViewNotificationBody {
    return {
      componentType: NOTIFICATION_COMPONENT_TYPES['DOCTOR_ROLE_REMOVED'],
      notificationData: {
        message: this.createNotificationMessagePayload(notification),
        dateCreated: notification.message_date_created,
        messageSid: notification.message_sid,
      },
    };
  }

  public createRouterUrl(notification: NotificationEvent): string {
    return `${ProjectLinks.LINK_PROFILE_VIEW}/${
      (notification.message_data as ConnectionInvitationNotification).initiator_account_id
    }`;
  }
}
